import { useState, useEffect, useCallback, useRef } from 'react';
import { Layout, Form, Row, Col, Radio, Input, InputNumber, Slider, Tooltip } from 'antd';
import { useIntercom } from 'react-use-intercom';
import PropTypes from 'prop-types';
import _ from 'lodash';

import ColorBox from 'components/ColorBox';
import UploadInfoIcon from 'components/Icons/UploadInfoIcon';
import WhatsappIcon from 'components/Icons/WhatsappIcon';
import MessageIcon from 'components/Icons/MessageIcon';
import CallIcon from 'components/Icons/CallIcon';
import { useProposalContext } from 'context/proposal';
import ResetItem from '../ResetItem/ResetItem';

import './LayoutMenu.scss';
import utils from 'utils/utils';
import { constructContactOptions } from '../../../RichEditor/utils/proposalSettings';

const CONTACT_CALL_BUTTON = 'block';

const defaultState = {
  position: 'normal',
  cover: 'top',
  contact: 'block',
  coverTextAlignment: 'left',
  logoPosition: 'left',
};

const defaultLayoutConfig = {
  spacing: '30.0px',
  backgroundColor: '#ffffff',
};

const getLayoutStructure = ({ isRtl, headerConfig, layout, layoutConfig }) => {
  const f = _.extend(
    {
      ...defaultState,
      coverTextAlignment: isRtl ? 'right' : 'left',
      logoPosition: isRtl ? 'right' : 'left',
      backgroundColor: '#ffffff',
    },
    headerConfig || layout || layoutConfig || {}
  );
  return f;
};

const LayoutMenu = ({ setDisablePreview }) => {
  const { user, proposal, saveTemplateProposal: saveProposal } = useProposalContext();

  const isRtl = utils.isRTLProposal(proposal);

  const { trackEvent } = useIntercom();

  // these are the lastest value to be respected
  // TODO these can be stored in proposal level instead of just in the side font menu
  const [cache, setCache] = useState({ count: {} });

  const [layoutConfig, setLayoutConfig] = useState({ ...defaultLayoutConfig });
  const [layout, setLayout] = useState({ ...defaultState });
  const [layoutTemp, setLayoutTemp] = useState({});
  const [contactButton, setContactButton] = useState('block');
  const [callButton, setCallButton] = useState('block');
  const [proposalContactOptions, setProposalContactOptions] = useState([]);

  const saveLayout = ({ layout, contactButton, callButton, layoutConfig, contactOptions }) => {
    window.ga('send', 'event', 'edit', 'edit-layout', window.location.href);

    trackEvent('layout-updated');

    const updatedProposal = {};
    if (layoutConfig) {
      updatedProposal['draft.layoutConfig'] = layoutConfig;
    }
    if (layout) {
      updatedProposal['draft.header.headerConfig'] = layout;
      updatedProposal['draft.titleFont'] = layout;
    }
    if (contactButton) {
      updatedProposal.contactButton = contactButton;
    }
    if (callButton) {
      updatedProposal.callButton = callButton;
    }
    if (contactOptions) {
      updatedProposal.contactOptions = contactOptions;
    }

    setDisablePreview('saving-layout');
    saveProposal(updatedProposal);
  };

  const handleEffect = (draftValue, key) => {
    // if draftValue is empty, return
    if (!draftValue) {
      return;
    }
    // if cache exists, return (those are lastest value in UI)
    if (cache?.[key]) {
      // if draftValue = cache
      if (_.isEqual(draftValue, cache[key])) {
        setCache((prev) => {
          const _cache = { ...prev };

          if (_cache.count?.[key] === 3) {
            // on 3rd hit remove cache
            delete _cache.count[key];
            delete _cache[key];
          } else {
            _cache.count[key] = (_cache.count[key] || 0) + 1;
          }

          return _cache;
        });
      }
      // updating values from draftValue might cause glitch in ui
      // as those are probably old values, wait untill cache is cleared
      return;
    }

    if (key === 'callButton') {
      setCallButton(draftValue || CONTACT_CALL_BUTTON);
    } else if (key === 'contactButton') {
      setContactButton(draftValue || CONTACT_CALL_BUTTON);
    } else if (key === 'headerConfig') {
      setLayout(getLayoutStructure({ isRtl, headerConfig: draftValue, layout }));
    } else if (key === 'contactOptions') {
      setProposalContactOptions(draftValue);
    } else if (key === 'layoutConfig') {
      setLayoutConfig(draftValue);
    }
  };

  const [showCustomSlider, setShowCustomSlider] = useState(false);
  const [customSpacingValue, setCustomSpacingValue] = useState(0);

  // const removeCache = (key) => {
  //   setCache((prev) => {
  //     const _cache = { ...prev };
  //     delete _cache[key];
  //     return _cache;
  //   });
  // };

  useEffect(() => {
    handleEffect(proposal?.draft?.header?.headerConfig, 'headerConfig');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [proposal.draft.header]);

  useEffect(() => {
    handleEffect(proposal?.contactButton, 'contactButton');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [proposal.contactButton]);

  useEffect(() => {
    handleEffect(proposal?.callButton, 'callButton');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [proposal.contactButton]);

  useEffect(() => {
    handleEffect(proposal?.contactOptions || [], 'contactOptions');

    // if (proposal?.contactOptions) {
    //   setProposalContactOptions(proposal.contactOptions || []);
    // }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [proposal.contactOptions]);

  useEffect(() => {
    handleEffect(proposal?.draft?.layoutConfig, 'layoutConfig');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [proposal.draft.layoutConfig]);

  useEffect(() => {
    if (!['16.0px', '30.0px', '54.0px'].includes(layoutConfig.spacing)) {
      setCustomSpacingValue(parseInt(layoutConfig.spacing, 10));
      setShowCustomSlider(true);
    } else {
      setShowCustomSlider(false);
    }
  }, [layoutConfig.spacing]);

  const onItemChange = ({ type, value }) => {
    if (type === 'contact') {
      setContactButton(value);
      saveLayout({ contactButton: value });
      temporaryCache({ contactButton: value });
    } else if (type === 'call') {
      setCallButton(value);
      saveLayout({ callButton: value });
      temporaryCache({ callButton: value });
    } else if (type === 'spacing') {
      const newConfig = {
        ...layoutConfig,
        [type]: value,
      };
      saveLayout({ layoutConfig: newConfig });
      temporaryCache({ [type]: value });
    } else if (type === 'backgroundColor') {
      const newConfig = {
        ...layoutConfig,
        [type]: value,
      };
      saveLayout({ layoutConfig: newConfig });
      temporaryCache({ [type]: value });
    } else {
      const updatedLayout = {
        ...layout,
        [type]: value,
      };

      if (type === 'coverTextAlignment' && value === 'custom') {
        updatedLayout['subtitleFontSize'] = '24px';
      }

      temporaryCache({ [type]: value });
      setLayout(updatedLayout);
      saveLayout({ layout: updatedLayout });
    }
  };

  const temporaryCache = (cache) => {
    // store latest value in temp, else useffect will keep changing the value
    setLayoutTemp((prev) => ({ ...prev, ...cache }));
  };

  const onReset = () => {
    window.ga('send', 'event', 'edit', 'reset-layout', window.location.href);

    trackEvent('reset-layout');

    setLayoutTemp({});

    setContactButton('block');
    setCallButton('block');
    const updatedLayout = {
      ...layout,
      position: 'normal',
      cover: 'top',
      contact: 'block',
      coverTextAlignment: isRtl ? 'right' : 'left',
      logoPosition: isRtl ? 'right' : 'left',
    };
    setLayout(updatedLayout);
    setLayoutConfig({ ...defaultLayoutConfig });

    const contactOptions = constructContactOptions(proposalContactOptions, user);
    setProposalContactOptions(contactOptions);

    saveLayout({
      layoutConfig: { ...defaultLayoutConfig },
      layout: updatedLayout,
      contactButton: 'block',
      callButton: 'block',
      contactOptions,
    });
  };

  //eslint-disable-next-line
  const debouncedSave = useCallback(
    _.debounce((tempProposalContactOptions) => {
      saveProposal({
        contactOptions: tempProposalContactOptions,
      });
    }, 1000),
    []
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const onCustomSpacingChange = useCallback(
    _.debounce(({ type, value }) => {
      const newConfig = {
        ...layoutConfig,
        [type]: value,
      };
      saveLayout({ layoutConfig: newConfig });
      temporaryCache({ [type]: value });
    }, 800),
    []
  );

  const onChangeSlider = (value) => {
    if (Number.isNaN(value)) {
      return;
    }
    const spacingHere = value + 'px';
    onCustomSpacingChange({ type: 'spacing', value: spacingHere });
    setCustomSpacingValue(value);
  };

  const handleContactOptionsChange = (name, value) => {
    let tempProposalContactOptions;
    if (!name) {
      tempProposalContactOptions = proposalContactOptions?.map((contactOption) => {
        if (value.includes(contactOption.option)) {
          return { ...contactOption, enabled: true };
        } else {
          return { ...contactOption, enabled: false };
        }
      });
    } else {
      tempProposalContactOptions = proposalContactOptions?.map((contactOption) => {
        if (name === contactOption.option) {
          return { ...contactOption, value: value };
        } else {
          return { ...contactOption };
        }
      });
    }

    setProposalContactOptions(tempProposalContactOptions);
    debouncedSave(tempProposalContactOptions);
  };

  const handleBackgroundColorChange = useRef(
    _.debounce((type, value) => {
      onItemChange({ type, value });
    }, 500)
  ).current;

  return (
    <Layout className="layout-menu">
      <h3 className="layout-menu-title">Cover</h3>
      <Form className="layout-menu-form" layout="vertical">
        <h3 className="divider">Cover Size</h3>
        <Radio.Group
          style={{ width: '100%' }}
          value={layoutTemp?.cover || layout.cover}
          onChange={(e) => onItemChange({ type: 'cover', value: e.target.value })}>
          <Row className="radio-group-row" gutter={[12, 12]}>
            <Col xs={12} title="Top First Page">
              <Form.Item colon={false}>
                <Radio value="top">Top First Page</Radio>
              </Form.Item>
            </Col>
            <Col xs={12}>
              <Form.Item colon={false}>
                <Radio value="full">Full Page</Radio>
              </Form.Item>
            </Col>
          </Row>
        </Radio.Group>

        <h3 className="divider">Cover Text Alignment</h3>
        <Radio.Group
          style={{ width: '100%' }}
          value={layoutTemp?.coverTextAlignment || layout.coverTextAlignment}
          onChange={(e) => onItemChange({ type: 'coverTextAlignment', value: e.target.value })}>
          <Row gutter={[12, 12]}>
            <Col xs={12}>
              <Form.Item colon={false}>
                <Radio value="left">Left</Radio>
              </Form.Item>
            </Col>
            <Col xs={12}>
              <Form.Item colon={false}>
                <Radio value="right">Right</Radio>
              </Form.Item>
            </Col>

            <Col xs={12}>
              <Form.Item colon={false}>
                <Radio value="center">Center</Radio>
              </Form.Item>
            </Col>
            <Col xs={12}>
              <Form.Item colon={false}>
                <Radio value="custom">Custom</Radio>
              </Form.Item>
            </Col>
          </Row>
        </Radio.Group>
        {/* <p className="layout-menu-form-description">
          Feel free to drag & drop any element on the cover
        </p> */}
      </Form>

      <h3 className="layout-menu-title">Body</h3>
      <Form className="layout-menu-form" layout="vertical">
        <h3 className="divider">Sections Background Color</h3>
        <Row gutter={[12, 12]}>
          <Col xs={12} className="color-box-col">
            <Form.Item label="" colon={false}>
              <ColorBox
                color={layoutConfig.backgroundColor || layout.backgroundColor}
                onChange={(value) => handleBackgroundColorChange('backgroundColor', value)}
              />
            </Form.Item>
          </Col>
        </Row>
        <h3 className="divider">Section Title Position</h3>
        <Radio.Group
          style={{ width: '100%' }}
          value={layoutTemp?.position || layout.position}
          onChange={(e) => onItemChange({ type: 'position', value: e.target.value })}>
          <Row className="radio-group-row" gutter={[12, 12]}>
            <Col xs={12} title="Side Normal">
              <Form.Item colon={false}>
                <Radio value="normal">Side Normal</Radio>
              </Form.Item>
            </Col>
            <Col xs={12} title="Side Small">
              <Form.Item colon={false}>
                <Radio value="large">Side Small</Radio>
              </Form.Item>
            </Col>
            <Col xs={12}>
              <Form.Item colon={false}>
                <Radio value="top">Top</Radio>
              </Form.Item>
            </Col>
            <Col xs={12}>
              <Form.Item colon={false}>
                <Radio value="top-middle">Top Middle</Radio>
              </Form.Item>
            </Col>
            <Col xs={12}>
              <Form.Item colon={false}>
                <Radio value="none">None</Radio>
              </Form.Item>
            </Col>
          </Row>
        </Radio.Group>

        <h3 className="divider">Space Between Sections</h3>
        <Radio.Group
          style={{ width: '100%' }}
          value={layoutConfig.spacing}
          onChange={(e) => onItemChange({ type: 'spacing', value: e.target.value })}>
          <Row gutter={[12, 12]}>
            <Col xs={12} title="Small">
              <Form.Item colon={false}>
                <Radio value="16.0px">Small</Radio>
              </Form.Item>
            </Col>
            <Col xs={12} title="Medium">
              <Form.Item colon={false}>
                <Radio value="30.0px">Medium</Radio>
              </Form.Item>
            </Col>
            <Col xs={12} title="Large">
              <Form.Item colon={false}>
                <Radio value="54.0px">Large</Radio>
              </Form.Item>
            </Col>
            <Col xs={12} title="Custom">
              <Form.Item colon={false}>
                <Radio
                  value={customSpacingValue + 'px'}
                  checked={showCustomSlider}
                  onClick={() => setShowCustomSlider(true)}>
                  Custom
                </Radio>
              </Form.Item>
            </Col>
          </Row>
        </Radio.Group>
        {showCustomSlider && (
          <Row className="slider-row">
            <Col xs={18}>
              <Slider
                min={1}
                max={100}
                onChange={onChangeSlider}
                value={customSpacingValue}
                className="custom-spacing-slider"
              />
            </Col>
            <Col xs={6}>
              <InputNumber
                min={1}
                max={100}
                maxLenght={3}
                value={customSpacingValue}
                className="custom-spacing-input"
                onChange={onChangeSlider}
                onInput={(e) => {
                  if (e.target.value > 100) {
                    e.target.value = 100;
                    setCustomSpacingValue(100);
                  }
                }}
                parser={(value) => (value ? value.replace(/[^\d]/g, '') : '')}
              />
            </Col>
          </Row>
        )}
      </Form>

      <h3 className="layout-menu-title">Contact Buttons</h3>
      <Form className="layout-menu-form" layout="vertical">
        {!proposalContactOptions?.length && (
          <>
            <h3 className="divider">Contact Button Visibility</h3>
            <Radio.Group
              style={{ width: '100%' }}
              value={layoutTemp?.contactButton || contactButton}
              onChange={(e) => onItemChange({ type: 'contact', value: e.target.value })}>
              <Row className="radio-group-row" gutter={[12, 12]}>
                <Col xs={12}>
                  <Form.Item colon={false}>
                    <Radio value="block">Visible</Radio>
                  </Form.Item>
                </Col>
                <Col xs={12}>
                  <Form.Item colon={false}>
                    <Radio value="none">Hidden</Radio>
                  </Form.Item>
                </Col>
              </Row>
            </Radio.Group>

            <h3 className="divider">Call Button Visibility</h3>
            <Radio.Group
              style={{ width: '100%' }}
              value={layoutTemp?.callButton || callButton}
              onChange={(e) => onItemChange({ type: 'call', value: e.target.value })}>
              <Row className="radio-group-row" gutter={[12, 12]}>
                <Col xs={12}>
                  <Form.Item colon={false}>
                    <Radio value="block">Visible</Radio>
                  </Form.Item>
                </Col>
                <Col xs={12}>
                  <Form.Item colon={false}>
                    <Radio value="none">Hidden</Radio>
                  </Form.Item>
                </Col>
              </Row>
            </Radio.Group>
          </>
        )}

        {proposalContactOptions?.length
          ? proposalContactOptions.map((contactOption) => (
              <>
                <h3 className="divider">
                  {contactOption.option === 'whatsapp' ? (
                    <WhatsappIcon className="contact-type-icon" />
                  ) : contactOption.option === 'email' ? (
                    <MessageIcon className="contact-type-icon" />
                  ) : (
                    <CallIcon className="contact-type-icon" />
                  )}
                  {`${contactOption.option} Button Visibility`}
                </h3>
                <Radio.Group
                  style={{ width: '100%' }}
                  value={contactOption.enabled}
                  onChange={(e) => {
                    const contactOptions = proposalContactOptions?.map((tempContactOption) => {
                      if (contactOption.option === tempContactOption.option) {
                        return { ...tempContactOption, enabled: e.target.value };
                      } else {
                        return { ...tempContactOption };
                      }
                    });

                    setProposalContactOptions(contactOptions);
                    setCache((prev) => ({ ...prev, contactOptions }));
                    saveProposal({ contactOptions });
                  }}>
                  <Row className="radio-group-row contact-type-row" gutter={[12, 12]}>
                    {[
                      { label: 'Visible', value: true },
                      { label: 'Hidden', value: false },
                    ].map((item) => (
                      <RadioButton key={item.label} {...item} />
                    ))}
                  </Row>

                  {contactOption.enabled && (
                    <Row className="layout-menu-input-wrapper">
                      <h3 className="divider">
                        <span>
                          {contactOption.option === 'whatsapp' ? 'WhatsApp' : contactOption.option}{' '}
                          Info
                        </span>
                        {contactOption.option !== 'email' && (
                          <Tooltip
                            title={
                              <>
                                Make sure to include the country code in the mobile number for{' '}
                                <br />
                                {contactOption.option === 'call'
                                  ? 'phone calls.'
                                  : 'WhatsApp messages.'}
                              </>
                            }
                            color="white">
                            <UploadInfoIcon className="info-icon" />
                          </Tooltip>
                        )}
                      </h3>
                      <Input
                        value={contactOption.value}
                        placeholder={
                          contactOption.option !== 'email'
                            ? 'e.g. +12163547758'
                            : contactOption.option
                        }
                        onChange={(e) =>
                          handleContactOptionsChange(contactOption.option, e.target.value)
                        }
                      />
                    </Row>
                  )}
                </Radio.Group>
              </>
            ))
          : null}
      </Form>

      <ResetItem onReset={onReset} />
    </Layout>
  );
};

LayoutMenu.propTypes = {
  setDisablePreview: PropTypes.func,
};
export default LayoutMenu;

function RadioButton({ label, value }) {
  return (
    <Col title={label} xs={12}>
      <Form.Item colon={false}>
        <Radio value={value}>{label}</Radio>
      </Form.Item>
    </Col>
  );
}
